<template>
  <div class="hd">
    <div v-if="loading" class="hd-info">Se încarcă...</div>

    <!-- Are cel puțin un modul cu dashboard -->
    <template v-else-if="available.length">
      <div v-if="available.length > 1" class="hd-bar">
        <div class="hd-chips">
          <button v-for="d in available" :key="d.modul" class="hd-chip" :class="{ active: current === d.modul }"
            @click="current = d.modul">{{ d.text }}</button>
        </div>
        <button class="hd-default" :disabled="isDefault" @click="setDefault">
          <i class="dx-icon dx-icon-pin" /> {{ isDefault ? 'Este implicit' : 'Setează ca implicit' }}
        </button>
      </div>
      <div class="hd-content">
        <component :is="currentComponent" />
      </div>
    </template>

    <!-- Niciun modul cu dashboard -> pagina de home ca înainte -->
    <WelcomeView v-else />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import CompanyServices from "@/services/CompanyService";
import Jwt from "@/services/JwtServices";
import WelcomeView from "./WelcomeView.vue";
import CRMDashboard from "./Comercial/CRM/CRMDashboard.vue";
import PersonalDashboard from "./ResurseUmane/PersonalDashboard.vue";
import DashboardStocuri from "./Stocuri/DashboardStocuri.vue";
import PortalDashboard from "./Portal/PortalDashboard.vue";

interface ModDef { modul: string; text: string; component: string; }

// Doar modulele care AU dashboard (AUTH_DrepturiModule.Nume), în ordinea de prioritate.
const MAP: ModDef[] = [
  { modul: "CRM", text: "CRM", component: "CRMDashboard" },
  { modul: "Resurse Umane", text: "Resurse Umane", component: "PersonalDashboard" },
  { modul: "Stocuri", text: "Stocuri", component: "DashboardStocuri" },
  { modul: "Portal HR", text: "Portal HR", component: "PortalDashboard" },
];

export default defineComponent({
  name: "HomeDashboard",
  components: { WelcomeView, CRMDashboard, PersonalDashboard, DashboardStocuri, PortalDashboard },
  data() {
    return {
      loading: true,
      accessible: [] as string[],
      current: "" as string,
    };
  },
  computed: {
    storageKey(): string {
      return "ERA_HomeDashboard_" + (Jwt.GetUsername?.() || "user");
    },
    available(): ModDef[] {
      return MAP.filter((m) => this.accessible.includes(m.modul));
    },
    currentComponent(): string {
      return this.available.find((d) => d.modul === this.current)?.component || "";
    },
    isDefault(): boolean {
      return localStorage.getItem(this.storageKey) === this.current;
    },
  },
  watch: {
    current() {
      this.emitBreadcrumb();
    },
  },
  async created() {
    try {
      const r = await CompanyServices.getModuleAccess();
      this.accessible = r.data?.Accessible || [];
    } catch {
      this.accessible = [];
    } finally {
      this.loading = false;
      this.pickCurrent();
      // Un singur modul cu dashboard -> îl setăm automat ca implicit (fără buton).
      if (this.available.length === 1 && this.current) this.setDefault();
      this.emitBreadcrumb();
    }
  },
  beforeUnmount() {
    window.dispatchEvent(new CustomEvent("era-home-breadcrumb", { detail: "" }));
  },
  methods: {
    pickCurrent() {
      const saved = localStorage.getItem(this.storageKey);
      const savedOk = saved && this.available.some((d) => d.modul === saved);
      this.current = savedOk ? (saved as string) : this.available[0]?.modul || "";
    },
    emitBreadcrumb() {
      const def = this.available.find((d) => d.modul === this.current);
      const label = def ? def.text + " – Dashboard" : "";
      window.dispatchEvent(new CustomEvent("era-home-breadcrumb", { detail: label }));
    },
    setDefault() {
      if (this.current) localStorage.setItem(this.storageKey, this.current);
    },
  },
});
</script>

<style scoped>
.hd { height: 100%; width: 100%; display: flex; flex-direction: column; }
.hd-info { flex: 1; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 15px; }
.hd-bar { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 12px 16px; background: #fff; border-bottom: 1px solid #e5e7eb; }
.hd-chips { display: flex; gap: 8px; flex-wrap: wrap; }
.hd-chip { border: 1px solid #e2e8f0; background: #f8fafc; color: #475569; border-radius: 20px; padding: 8px 18px; font-size: 14px; font-weight: 600; cursor: pointer; }
.hd-chip.active { background: linear-gradient(90deg, #ff2c21, #ff6a4a); border-color: transparent; color: #fff; }
.hd-default { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #e2e8f0; background: #fff; color: #2563eb; border-radius: 10px; padding: 8px 14px; font-size: 13px; font-weight: 600; cursor: pointer; }
.hd-default:disabled { color: #94a3b8; cursor: default; }
.hd-content { flex: 1; min-height: 0; overflow: auto; }
</style>
